import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin } from "lucide-react";
import InviteModal from "./InviteModal";
import ContributionLabel from "./ContributionLabel";
import { NAV } from "../lib/nav";
import { WATER } from "../lib/nav";
import Marquee from "./Marquee";
import { BrandStack, SHOW_EXPERIENCES_SITE, EXPERIENCES_SITE } from "./Brand";
import { NavLabel, jump } from "./Header";
import { WAYS_IN_IG } from "../lib/media";

const IG = "https://www.instagram.com/luxewellexperiences/";
const SWIRL = "/photos/swirl.png";
const IG_PHOTOS = ["16-ig-strip.jpeg", "02-home-conciergerie.jpeg", "10-connectivity-lounge.jpeg", "italy-amalfi-infinity-pool.jpg", "05-conciergerie-longevity.jpeg", "09-connectivity-list.jpeg", "gives-children-hands.jpg", "06-conciergerie-leisure.jpeg", "01-home-consultancy.jpeg|center 12%", "conciergerie-landscape.jpg", "wellthspan-retreat-group.jpg", "nitasha-garden-laptop.jpg", "11-connectivity-lifestyle-wedding.jpeg", "08-lw-living.jpeg", "03-home-connectivity.jpeg", "07-lw-love.jpeg", "08b-lw-learning.jpeg", ...WAYS_IN_IG];

function InstaStrip() {
  return (
    <div className="relative pt-10 pb-2" data-testid="insta-strip">
      <p className="text-center mb-8"><a href={IG} target="_blank" rel="noreferrer" data-testid="insta-handle" className="plain-white eyebrow !text-[0.7rem] !text-white border-b border-gold/40 pb-1.5 hover:!text-goldbright transition-colors">Get social with us · @luxewellexperiences</a></p>
      <Marquee duration={IG_PHOTOS.length * 9}>
        {IG_PHOTOS.map((entry) => { const [p, pos] = entry.split("|"); return (
          <a key={p} href={IG} target="_blank" rel="noreferrer" className="block w-40 h-40 lg:w-48 lg:h-48 shrink-0 mr-2 overflow-hidden">
            <img src={p.startsWith("/") ? p : `/photos/${p}`} alt="LUXEWELL on Instagram" loading="lazy" style={{ objectPosition: pos || "center 60%" }} className="aperol w-full h-full object-cover [backface-visibility:hidden] hover:scale-105 transition-transform duration-700" />
          </a>
        ); })}
      </Marquee>
    </div>
  );
}

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/luxewellexperiences/" },
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/in/nitashasarin/" },
];

const COLS = NAV.filter((n) => n.items);
const SOLO = [{ key: "contribution", to: "/founders#luxewell-gives" }, ...NAV.filter((n) => !n.items), { key: "contact", label: "Connect", to: "/contact" }];

export default function Footer() {
  const [invite, setInvite] = useState(null);
  return (
    <footer data-testid="site-footer" className="footer-pop relative text-white font-medium border-t border-gold/60 overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center water-drift" style={{ backgroundImage: `url(${WATER})` }} />
      <div className="absolute inset-0 bg-[#37502f]/72" />
      <InstaStrip />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid gap-12 lg:grid-cols-5">
          <div>
            <Link to="/" className="inline-flex items-center">
              <img src="/logo-white.png" alt="LUXEWELL" className="no-grade h-[62px] w-auto object-contain" />
            </Link>
            <p className="mt-4 font-headline italic text-xl text-white leading-snug max-w-xs" data-testid="footer-tagline">
              Luxury Wellness<br />with a Lasting Legacy.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="eyebrow !text-[0.62rem] !text-white mr-1 whitespace-nowrap" data-testid="footer-social-label">Get social with us</span>
              {SOCIALS.map(({ icon: Icon, label, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="w-9 h-9 flex items-center justify-center border border-gold/50 text-ivory hover:text-ink hover:bg-gold hover:border-gold transition-colors">
                  <Icon size={16} />
                </a>
              ))}
            </div>
            <button onClick={() => setInvite({})} data-testid="footer-join-cta"
              className="plain-white mt-6 eyebrow !text-[0.62rem] !text-ivory border-b border-gold/60 pb-1 hover:!text-goldbright transition-colors">Enter the house →</button>
          </div>
          {COLS.map((c) => (
            <div key={c.key}>
              <Link to={c.to} onClick={jump(c.to)} data-testid={`footer-head-${c.key}`} className="eyebrow !text-[0.74rem] block mb-5 pb-2 border-b border-gold/40 !text-white hover:!text-goldbright transition-colors">{c.label}</Link>
              <ul className="space-y-3">
                {c.items.map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} onClick={jump(to)} data-testid={`footer-link-${label.replace(/[^a-zA-Z]+/g, "-").toLowerCase().replace(/-$/, "")}`}
                      className="plain-white text-[0.95rem] text-white hover:text-goldbright transition-colors"><NavLabel l={label} /></Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <ul className="space-y-5">
              {SOLO.map((n) => (
                <li key={n.key}>
                  <Link to={n.to} onClick={jump(n.to)} data-testid={`footer-link-${n.key}`} className="plain-white eyebrow !text-[0.74rem] !text-white hover:!text-goldbright transition-colors">
                    {n.key === "contribution" ? <ContributionLabel /> : n.label}
                  </Link>
                </li>
              ))}
              {SHOW_EXPERIENCES_SITE && (
                <li><a href={EXPERIENCES_SITE} target="_blank" rel="noreferrer" data-testid="footer-experiences-site" className="plain-white eyebrow !text-[0.74rem] !text-white hover:!text-goldbright transition-colors">LUXEWELL Experiences</a></li>
              )}
            </ul>
          </div>
        </div>

        <div className="hairline mt-14 mb-6" />
        <div className="text-center text-[0.95rem] text-white max-w-3xl mx-auto mb-8" data-testid="footer-report">
          <p className="eyebrow !text-white mb-3">The LUXEWELL Report</p>
          <p>Emerging insights, connected ecosystems and invitations to curated experiences across</p>
          <p>the global luxury wellness landscape.</p>
          <button onClick={() => setInvite({})} data-testid="footer-report-cta" className="plain-white mt-4 eyebrow !text-[0.62rem] !text-white border-b border-gold/60 pb-1 hover:!text-goldbright transition-colors">Be first in the know →</button>
        </div>
        <p className="text-center mb-4" data-testid="footer-email">
          <Link to="/contact" data-testid="footer-connect-cta" className="plain-white eyebrow !text-[0.62rem] !text-white border-b border-gold/60 pb-1 hover:!text-goldbright transition-colors">Connect with us</Link>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 max-w-2xl mx-auto text-center text-[0.8rem] tracking-wide text-white mb-6" data-testid="footer-contact">
          <div><p className="eyebrow !text-[0.6rem]">Consultancy · Conciergerie<br />Connectivity</p><a href="mailto:explore@luxewellexperiences.com" data-testid="footer-email-explore" className="plain-white mt-1 inline-block">explore@<wbr />luxewellexperiences.com</a></div>
          <div><p className="eyebrow !text-[0.6rem]">Speaking · Media<br />Partnerships</p><a href="mailto:team@globalifegroup.com" data-testid="footer-email-team" className="plain-white mt-1 inline-block">team@<wbr />globalifegroup.com</a></div>
        </div>
        <p className="text-[0.78rem] tracking-wide text-white text-center mb-4">
          <Link to="/login" data-testid="footer-member-signin" className="plain-white">Member sign in</Link>
          {" · "}
          <Link to="/privacy" data-testid="footer-link-privacy" className="plain-white">Privacy</Link>
        </p>
        <BrandStack className="mt-10 mb-8 text-center text-white !text-lg sm:!text-xl !font-normal !leading-snug [&>p+p]:!mt-0" testid="footer-signoff" line2="Join Us… We're Redefining Success.™" />
        <p className="text-[0.7rem] sm:text-[0.78rem] tracking-wide text-white text-center whitespace-nowrap" data-testid="footer-bottom-line">
          House of LUXEWELL & LUXEWELL Experiences
        </p>
        <p className="mt-1 text-[0.78rem] tracking-wide text-white text-center" data-testid="footer-division">A division of Global Life Group</p>
        <p className="mt-1 text-[0.78rem] tracking-wide text-white text-center" data-testid="footer-rights">© 2026 · All rights reserved</p>
        {SWIRL && <img src={SWIRL} alt="" data-testid="footer-swirl" className="no-grade block mx-auto mt-5 w-9 h-auto" />}
      </div>
      <InviteModal open={invite !== null} onClose={() => setInvite(null)} defaultInterests={invite?.interests || []} />
    </footer>
  );
}
